import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, CircularProgress, Alert,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useSnackbar } from "notistack";
import { add_student_api } from "../../DAL/FlightSchools/FlightSchools";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff", borderRadius: "9px", color: "#1a1a1a",
    "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
    "&:hover fieldset": { borderColor: "rgba(26,26,26,0.28)" },
    "&.Mui-focused fieldset": { borderColor: "#c01e2e", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { color: "#1a1a1a" },
  "& .MuiInputLabel-root": { color: "#aaa", fontSize: "14px" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#c01e2e" },
};

export default function AddStudentDialog({ open, onClose, schoolId, school, onSuccess }) {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ first_name: "", last_name: "", email: "" });

  const handleChange = (field) => (e) => setFormData((p) => ({ ...p, [field]: e.target.value }));

  const handleClose = () => { setFormData({ first_name: "", last_name: "", email: "" }); onClose(); };

  const handleSubmit = async () => {
    if (!formData.first_name.trim()) { enqueueSnackbar("First name is required", { variant: "error" }); return; }
    if (!formData.last_name.trim())  { enqueueSnackbar("Last name is required", { variant: "error" }); return; }
    if (!formData.email.trim())      { enqueueSnackbar("Email is required", { variant: "error" }); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      enqueueSnackbar("Please enter a valid email address", { variant: "error" }); return;
    }
    setIsSubmitting(true);
    try {
      const result = await add_student_api(schoolId, formData);
      if (result.code === 200 || result.code === 201) {
        enqueueSnackbar("Student added successfully. Invite email sent.", { variant: "success" });
        handleClose(); onSuccess?.();
      } else enqueueSnackbar(result.message || "Failed to add student", { variant: "error" });
    } catch { enqueueSnackbar("An error occurred", { variant: "error" }); }
    finally { setIsSubmitting(false); }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { backgroundColor: "#ffffff", border: "1px solid rgba(26,26,26,0.09)", borderRadius: "14px" } }}>
      <DialogTitle sx={{ color: "#1a1a1a", fontFamily: "'Rubik', sans-serif", display: "flex", alignItems: "center", gap: 1 }}>
        <PersonAddIcon sx={{ color: "#c01e2e" }} /> Add New Student
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3, borderRadius: "9px" }}>
          An invitation email will be sent to the student with instructions to set up their account.
        </Alert>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField fullWidth label="First Name *" value={formData.first_name} onChange={handleChange("first_name")} sx={inputSx} autoFocus />
            <TextField fullWidth label="Last Name *"  value={formData.last_name}  onChange={handleChange("last_name")}  sx={inputSx} />
          </Box>
          <TextField fullWidth label="Email Address *" type="email" value={formData.email} onChange={handleChange("email")} sx={inputSx} />
        </Box>
        {school && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: "#F7F4EF", border: "1px solid rgba(26,26,26,0.08)", borderRadius: "10px" }}>
            <Typography sx={{ color: "#aaa", fontSize: "12px", mb: 1, fontFamily: "'Rubik', sans-serif", textTransform: "uppercase", letterSpacing: "0.3px" }}>Account Details</Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>School</Typography>
              <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{school.name}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.75 }}>
              <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>Account Duration</Typography>
              <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{school.default_account_duration_months || 12} months</Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} sx={{ color: "#6b6b6b", textTransform: "none", fontFamily: "'Rubik', sans-serif" }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}
          sx={{ backgroundColor: "#c01e2e", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "8px", boxShadow: "none", "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}>
          {isSubmitting ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Add & Send Invite"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
